import type { MouseEvent, ReactElement } from 'react';

import { GameCommunicationBetSender } from '../../../../controllers/gameCommunication/senders/GameCommunicationBetSender';
import { Logger } from '../../../../logger/Logger';
import { useDoubleBetEnabled } from '../../../../store/apiHooks/useDoubleBetEnabled';
import { useCoreDispatch } from '../../../../store/CoreStore';
import { useBetSlice } from '../../../../store/slices/bets/selectors';
import { betActions } from '../../../../store/slices/bets/slice';
import { useGameStateSlice } from '../../../../store/slices/game/selectors';
import { type GameButtonProps, GameButtonWithTooltip } from '../../../game/gameButton/gameButton';
import { Icons } from '../../../icons/iconNames';

const betSender = new GameCommunicationBetSender();

export interface ReBetControlConnectedProps extends Omit<GameButtonProps, 'icon'> {
  tooltip?: string;
}

export const ReBetControlConnected = ({
  disabled,
  onClick,
  tooltip,
  ...rest
}: ReBetControlConnectedProps): ReactElement => {
  const dispatch = useCoreDispatch();
  const isDoubleBetEnabled = useDoubleBetEnabled();

  const bets = useBetSlice.bets();
  const isBetsOpenStatus = useGameStateSlice.isBetsOpenStatus();
  const decimals = useGameStateSlice.getCurrencyInfo()?.digitsNumber || 2;
  const balance = useGameStateSlice.getDeductibleBalance();
  const isReBetEnabled = useBetSlice.isReBetEnabled(balance, decimals);
  const totalBet = useBetSlice.totalBet();

  const handleReBet = (event: MouseEvent<HTMLButtonElement>): void => {
    dispatch(betActions.reBet({ balance, decimals }));
    Logger.logEvent('click', 'reBet', Date.now(), 1, '', false);
    betSender.dispatch();
    onClick?.(event);
  };

  const handleDoubleBet = (event: MouseEvent<HTMLButtonElement>): void => {
    dispatch(betActions.doubleBet({ balance, decimals }));
    Logger.logEvent('click', 'doubleBet', Date.now(), 1, '', false);
    betSender.dispatch();
    onClick?.(event);
  };

  const isDisabled = disabled || !isBetsOpenStatus;
  const doubleBetDisabled = isDisabled || !isDoubleBetEnabled;
  const reBetDisabled = isDisabled || !isReBetEnabled;

  return bets && totalBet > 0 ? (
    <GameButtonWithTooltip
      data-testid="double-bet-button"
      border
      disabled={doubleBetDisabled}
      icon={Icons.DoubleBet}
      onClick={handleDoubleBet}
      pulse
      sound="doubleBet"
      tooltipOptions={{
        disabled: doubleBetDisabled,
        text: tooltip || 'DOUBLE',
      }}
      {...rest}
    />
  ) : (
    <GameButtonWithTooltip
      data-testid="re-bet-button"
      border
      disabled={reBetDisabled}
      icon={Icons.Update}
      onClick={handleReBet}
      pulse
      sound="reBet"
      tooltipOptions={{
        disabled: reBetDisabled,
        text: tooltip || 'REBET',
      }}
      {...rest}
    />
  );
};
